/**
 * Helper functions for authentication in frontend
 */

import { supabase } from "./supabase";

/**
 * Get the current access token from Supabase session
 * Returns null if no valid session exists
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error || !session) {
      return null;
    }

    return session.access_token;
  } catch (error: any) {
    if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
      return null;
    }
    console.error("Error getting access token:", error);
    return null;
  }
}

/**
 * Get authorization header for API requests
 * Returns null if no valid session exists
 */
export async function getAuthHeader(): Promise<Record<string, string> | null> {
  const token = await getAccessToken();

  if (!token) {
    return null;
  }

  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * Fetch with automatic authentication
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const authHeader = await getAuthHeader();

  const headers = {
    ...options.headers,
    ...(authHeader || {}),
  };

  return fetch(url, {
    ...options,
    headers,
  });
}
